// Input sanitization utilities to prevent SMTP injection attacks

/**
 * Sanitizes text input to prevent SMTP header injection
 * @param {string} input - The input text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeTextInput = (input) => {
  if (!input || typeof input !== "string") {
    return "";
  }

  // Remove SMTP header injection characters and sequences
  return input
    .replace(/[\r\n]+/g, " ") // Replace line breaks with spaces
    // eslint-disable-next-line no-control-regex
    .replace(/\x00/g, "") // Remove null bytes
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim()
    .substring(0, 1000); // Limit length to prevent abuse
};

/**
 * Validates and sanitizes email addresses
 * @param {string} email - The email address to validate
 * @returns {object} - {isValid: boolean, sanitizedEmail: string, error: string}
 */
export const validateAndSanitizeEmail = (email) => {
  if (!email || typeof email !== "string") {
    return { isValid: false, sanitizedEmail: "", error: "Email is required" };
  }

  // Basic sanitization
  const sanitizedEmail = email.trim().toLowerCase();

  // Check for SMTP injection patterns
  const smtpInjectionPatterns = [
    /[\r\n]/, // Line breaks
    /\bcc:/i, // CC header
    /\bbcc:/i, // BCC header
    /\bto:/i, // TO header
    /\bfrom:/i, // FROM header
    /\bsubject:/i, // SUBJECT header
    /\bcontent-type:/i, // Content-Type header
    /\bmime-version:/i, // MIME-Version header
    // eslint-disable-next-line no-control-regex
    /\x00/, // Null bytes
  ];

  // Check for injection patterns
  for (const pattern of smtpInjectionPatterns) {
    if (pattern.test(sanitizedEmail)) {
      return {
        isValid: false,
        sanitizedEmail: "",
        error: "Invalid email format detected",
      };
    }
  }

  // Enhanced email validation regex
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(sanitizedEmail)) {
    return {
      isValid: false,
      sanitizedEmail: "",
      error: "Please enter a valid email address",
    };
  }

  // Additional length and format checks
  if (sanitizedEmail.length > 254) {
    return {
      isValid: false,
      sanitizedEmail: "",
      error: "Email address is too long",
    };
  }

  return { isValid: true, sanitizedEmail, error: null };
};

/**
 * Sanitizes name input to prevent injection attacks
 * @param {string} name - The name to sanitize
 * @returns {object} - {isValid: boolean, sanitizedName: string, error: string}
 */
export const validateAndSanitizeName = (name) => {
  if (!name || typeof name !== "string") {
    return { isValid: false, sanitizedName: "", error: "Name is required" };
  }

  // Sanitize the name
  const sanitizedName = sanitizeTextInput(name);

  // Check minimum length
  if (sanitizedName.length < 2) {
    return {
      isValid: false,
      sanitizedName: "",
      error: "Name must be at least 2 characters long",
    };
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /[<>]/, // HTML tags
    /javascript:/i, // JavaScript protocol
    /data:/i, // Data protocol
    /vbscript:/i, // VBScript protocol
    /\bcc:/i, // Email headers
    /\bbcc:/i,
    /\bto:/i,
    /\bfrom:/i,
    /\bsubject:/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(sanitizedName)) {
      return {
        isValid: false,
        sanitizedName: "",
        error: "Name contains invalid characters",
      };
    }
  }

  return { isValid: true, sanitizedName, error: null };
};

/**
 * Sanitizes message body to prevent injection attacks
 * @param {string} body - The message body to sanitize
 * @returns {object} - {isValid: boolean, sanitizedBody: string, error: string}
 */
export const validateAndSanitizeBody = (body) => {
  if (!body || typeof body !== "string") {
    return { isValid: false, sanitizedBody: "", error: "Message is required" };
  }

  // Basic sanitization while preserving intended line breaks
  let sanitizedBody = body
    // eslint-disable-next-line no-control-regex
    .replace(/\x00/g, "") // Remove null bytes
    .replace(/[\r\n]{3,}/g, "\n\n") // Limit consecutive line breaks
    .trim();

  // Check minimum length
  if (sanitizedBody.length < 10) {
    return {
      isValid: false,
      sanitizedBody: "",
      error: "Message must be at least 10 characters long",
    };
  }

  // Limit maximum length
  if (sanitizedBody.length > 5000) {
    sanitizedBody = sanitizedBody.substring(0, 5000);
  }

  // Check for SMTP header injection in body
  const headerInjectionPatterns = [
    /^(to|from|cc|bcc|subject|content-type|mime-version):/im,
    /\n(to|from|cc|bcc|subject|content-type|mime-version):/im,
    /\r(to|from|cc|bcc|subject|content-type|mime-version):/im,
  ];

  for (const pattern of headerInjectionPatterns) {
    if (pattern.test(sanitizedBody)) {
      return {
        isValid: false,
        sanitizedBody: "",
        error: "Message contains invalid content",
      };
    }
  }

  return { isValid: true, sanitizedBody, error: null };
};

/**
 * Comprehensive validation for contact form data
 * @param {object} formData - The form data to validate
 * @returns {object} - {isValid: boolean, sanitizedData: object, errors: object}
 */
export const validateContactForm = (formData) => {
  const { name, email, body } = formData;
  const errors = {};
  const sanitizedData = {};

  // Validate name
  const nameValidation = validateAndSanitizeName(name);
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error;
  } else {
    sanitizedData.name = nameValidation.sanitizedName;
  }

  // Validate email
  const emailValidation = validateAndSanitizeEmail(email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  } else {
    sanitizedData.email = emailValidation.sanitizedEmail;
  }

  // Validate body
  const bodyValidation = validateAndSanitizeBody(body);
  if (!bodyValidation.isValid) {
    errors.body = bodyValidation.error;
  } else {
    sanitizedData.body = bodyValidation.sanitizedBody;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    sanitizedData,
    errors,
  };
};

/**
 * Validation for newsletter subscription
 * @param {string} email - The email address for newsletter subscription
 * @returns {object} - {isValid: boolean, sanitizedEmail: string, error: string}
 */
export const validateNewsletterEmail = (email) => {
  return validateAndSanitizeEmail(email);
};
